import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAG") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: 0,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.18)" : "#eef0ff",
    "& .MuiTableCell-head": {
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "0.8125rem",
    },
  },
  tableHeaderCell: {
    backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.18)" : "#eef0ff",
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
  },
  // ✅ ESTILOS PARA CHIPS DE ETIQUETAS
  tagChip: {
    fontWeight: "bold",
    fontSize: "0.75rem",
    minWidth: "80px",
    textShadow: "1px 1px 1px rgba(0,0,0,0.3)",
  },
  // ✅ ESTILOS PARA ACCIONES
  actionButton: {
    padding: theme.spacing(0.5),
    margin: theme.spacing(0, 0.5),
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  editButton: {
    color: "#1976d2",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
  },
  deleteButton: {
    color: "#f44336",
    "&:hover": {
      backgroundColor: "rgba(244, 67, 54, 0.04)",
    },
  },
}));

const Tags = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("tags");

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber },
      });
      
      // ✅ Datos de etiquetas procesados
      
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchTags]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    socket.on(`company-${user.companyId}-tag`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAG", payload: +data.tagId });
      }
    });

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${user.companyId}-tag`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
    };
  }, [socketManager, user]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchTags();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
      />
      <MainHeader>
        <Title>{i18n.t("tags.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenTagModal}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} /> 
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {tags.map((tag) => (
              <RecordCard
                key={`tag-card-${tag.id}`}
                title={tag.name}
                actions={
                  tag.profileId != null ? (
                    <Chip
                      label={i18n.t("tags.readOnly")}
                      size="small"
                      style={{ backgroundColor: "#e0e0e0", color: "#666", fontSize: "0.65rem" }}
                    />
                  ) : (
                    <>
                      <IconButton
                        className={`${classes.actionButton} ${classes.editButton}`}
                        size="small"
                        onClick={() => handleEditTag(tag)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingTag(tag);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </>
                  )
                }
              >
                <Chip
                  className={classes.tagChip}
                  style={{ backgroundColor: tag.color, color: "white" }}
                  label={tag.name}
                  size="small"
                />
                <RecordCardMeta>
                  {i18n.t("tags.table.taggedTickets")}: {tag.ticketsCount || 0}
                </RecordCardMeta>
                <RecordCardMeta>
                  {tag.kanban === 1 ? i18n.t("tags.kanbanActive") : i18n.t("tags.kanbanInactive")}
                  {" · "}
                  {tag.profileId != null ? i18n.t("tags.originProfile") : i18n.t("tags.originManual")}
                </RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("tags.table.tagName")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("tags.table.taggedTickets")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("tags.table.kanban")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("tags.table.origin")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("tags.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tags.map((tag) => (
                <TableRow key={tag.id} hover>
                  <TableCell align="center">
                    <Chip
                      className={classes.tagChip}
                      style={{
                        backgroundColor: tag.color,
                        color: "white",
                      }}
                      label={tag.name}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <strong>{tag.ticketsCount || 0}</strong>
                  </TableCell>
                  <TableCell align="center">
                    {tag.kanban === 1 ? (
                      <Chip
                        label={i18n.t("tags.kanbanActive")}
                        size="small"
                        style={{
                          backgroundColor: "#4caf50",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    ) : (
                      <Chip
                        label={i18n.t("tags.kanbanInactive")}
                        size="small"
                        style={{
                          backgroundColor: "#9e9e9e",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {tag.profileId != null ? (
                      <Chip
                        label={i18n.t("tags.originProfile")}
                        size="small"
                        style={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                        }}
                        title={i18n.t("tags.profileTagTitle")}
                      />
                    ) : (
                      <Chip
                        label={i18n.t("tags.originManual")}
                        size="small"
                        style={{
                          backgroundColor: "#9e9e9e",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                        }}
                        title={i18n.t("tags.manualTagTitle")}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {tag.profileId != null ? (
                      <Chip
                        label={i18n.t("tags.readOnly")}
                        size="small"
                        style={{ backgroundColor: "#e0e0e0", color: "#666", fontSize: "0.65rem" }}
                        title={i18n.t("tags.readOnlyHint")}
                      />
                    ) : (
                      <>
                        <IconButton
                          className={`${classes.actionButton} ${classes.editButton}`}
                          size="small"
                          onClick={() => handleEditTag(tag)}
                          title={i18n.t("tags.tooltips.editTag")}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingTag(tag);
                          }}
                          title={i18n.t("tags.tooltips.deleteTag")}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Tags;
